import React, { useState, useRef, useEffect } from 'react';
import ReviewNotes from './ReviewNotes';
import { getDynamicDisplayName } from '../data/mockProject';
import { uploadRecording } from '../api/projectApi';
import { validateWavFile, formatValidationErrors, generateFileHash } from '../utils/wavValidation';
import { useUploadContext } from '../context/UploadContext';
import {
  MAX_UPLOADS_PER_SLOT,
  canRetryUploadLimit,
  getUploadLimitMessage,
  isFileOverSizeLimit,
  matchUploadLimitReason,
} from '../utils/uploadLimits';

const DynamicCard = ({
  dynamic,
  articulationName,
  scoreUrl,
  slug,
  accessToken,
  onUploadComplete,
  portalUploadBlockedReason = null,
  previewLimitReason = null,
}) => {
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, success, error, confirm, refused
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [limitReason, setLimitReason] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  
  // Get upload context for duplicate detection
  const { registerUpload, clearUpload, isDuplicate } = useUploadContext();

  const needsAttention = 
    dynamic.status === 'revision_requested' || 
    dynamic.status === 'rejected';

  const uploadCount = Number(dynamic.upload_count) || 0;
  const slotLocked =
    dynamic.upload_locked === true || uploadCount >= MAX_UPLOADS_PER_SLOT;
  const blockedReason =
    portalUploadBlockedReason ||
    (slotLocked ? 'slot_locked' : null) ||
    previewLimitReason ||
    null;

  // Allow upload unless a transfer is in progress or the box is hard-blocked.
  // An error on the box does not block a new drop.
  const hardBlocked =
    blockedReason === 'slot_locked' ||
    blockedReason === 'portal_full' ||
    blockedReason === 'bucket_full';
  const canUpload = uploadState !== 'uploading' && uploadState !== 'checking' && !hardBlocked;

  // Step 1 preview / server-driven lock: show refusal without needing an upload attempt
  useEffect(() => {
    if (!blockedReason) return;
    setUploadState('refused');
    setLimitReason(blockedReason);
    setUploadError(getUploadLimitMessage(blockedReason));
  }, [blockedReason]);

  // Get dynamic display name (e.g., "pp - extra soft") or use articulation name if no dynamic
  const dynamicDisplayName = getDynamicDisplayName(dynamic);
  
  // Full display name for the recording
  const displayName = dynamicDisplayName 
    ? `${articulationName} - ${dynamicDisplayName}`
    : articulationName;

  const showLimitRefusal = (reason) => {
    setLimitReason(reason);
    setUploadState('refused');
    setUploadError(getUploadLimitMessage(reason));
  };

  const handleFileSelect = async (file, skipConfirmation = false) => {
    if (!file || !canUpload) return;

    if (isFileOverSizeLimit(file.size)) {
      showLimitRefusal('file_too_large');
      return;
    }

    setUploadState('checking');
    setUploadProgress(0);
    setUploadError(null);
    setLimitReason(null);

    // Compute file hash once for duplicate detection and server storage
    let fileHash = null;
    try {
      fileHash = await generateFileHash(file);
      if (isDuplicate(fileHash, dynamic.id)) {
        setLimitReason(null);
        setUploadState('error');
        setUploadError('This file has already been uploaded to another field. Each recording must be unique.');
        return;
      }
    } catch (err) {
      console.error('Failed to check for duplicate:', err);
      // Continue with upload even if hash check fails
    }

    // Validate the file before uploading (including minimum duration if specified)
    const validation = await validateWavFile(
      file, 
      dynamic.expected_filename, 
      dynamic.minimum_duration_seconds
    );
    if (!validation.valid) {
      setLimitReason(null);
      setUploadState('error');
      setUploadError(formatValidationErrors(validation.errors));
      return;
    }

    // If filename needs confirmation and we haven't confirmed yet
    if (validation.needsConfirmation && !skipConfirmation) {
      setPendingFile({ file, fileHash });
      setConfirmMessage(validation.confirmationMessage);
      setUploadState('confirm');
      return;
    }

    // Proceed with upload
    await proceedWithUpload(file, fileHash);
  };

  const proceedWithUpload = async (file, fileHash = null) => {
    setUploadState('uploading');
    setUploadProgress(0);
    setUploadError(null);
    setLimitReason(null);
    setPendingFile(null);
    setConfirmMessage(null);

    try {
      // Clear any previous upload for this field
      clearUpload(dynamic.id);
      
      // Upload with file hash for server-side storage
      await uploadRecording(
        slug,
        dynamic.id,
        file,
        (progress) => setUploadProgress(progress),
        fileHash,
        accessToken,
      );
      
      // Register the file hash locally to prevent duplicates in this session
      if (fileHash) {
        registerUpload(fileHash, dynamic.id);
      }
      
      setUploadState('success');
      
      // Refresh project data after successful upload
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      console.error('Upload failed:', err);
      const matched = matchUploadLimitReason(err.message);
      if (matched) {
        showLimitRefusal(matched);
        return;
      }
      setLimitReason(null);
      setUploadState('error');
      setUploadError(err.message || 'Upload failed. Please try again.');
    }
  };

  const handleConfirmUpload = () => {
    if (pendingFile) {
      proceedWithUpload(pendingFile.file, pendingFile.fileHash);
    }
  };

  const handleCancelConfirm = () => {
    setPendingFile(null);
    setConfirmMessage(null);
    setUploadState('idle');
  };

  const handleCardDragEnter = (e) => {
    e.preventDefault();
    if (!canUpload) return;
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleCardDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = canUpload ? 'copy' : 'none';
    }
  };

  const handleCardDragLeave = (e) => {
    e.preventDefault();
    if (!canUpload) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleCardDrop = (e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (!canUpload) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (canUpload && uploadState !== 'uploading') {
      fileInputRef.current?.click();
    }
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Render the status box content based on status and upload state
  const renderStatusBox = () => {
    if (uploadState === 'checking') {
      return (
        <div className="status-box status-box-uploading">
          <span className="status-box-text">Checking file…</span>
        </div>
      );
    }

    // Show upload progress if uploading
    if (uploadState === 'uploading') {
      return (
        <div className="status-box status-box-uploading">
          <div className="upload-progress-bar">
            <div 
              className="upload-progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className="status-box-text">Uploading... {uploadProgress}%</span>
        </div>
      );
    }

    // Limit refusals (file size / slot lock / portal or bucket full)
    if (uploadState === 'refused' && uploadError) {
      const retryable = canRetryUploadLimit(limitReason);
      return (
        <div
          className={`status-box status-box-error ${retryable ? 'status-box-upload' : 'status-box-refused'}`}
          onClick={retryable ? handleClick : undefined}
          style={retryable ? undefined : { cursor: 'default' }}
        >
          <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span className="status-box-text">
            {uploadError}
            {retryable ? ' — Click to choose another file' : ''}
          </span>
        </div>
      );
    }

    // Show error if upload failed
    if (uploadState === 'error') {
      return (
        <div 
          className="status-box status-box-upload status-box-error"
          onClick={handleClick}
        >
          <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span className="status-box-text">{uploadError} — Click to retry</span>
        </div>
      );
    }

    // Show confirmation prompt for filename mismatch
    if (uploadState === 'confirm') {
      return (
        <div className="status-box status-box-confirm">
          <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="status-box-text">{confirmMessage}</span>
          <div className="status-box-actions">
            <button className="confirm-btn confirm-btn-yes" onClick={handleConfirmUpload}>
              Yes, upload anyway
            </button>
            <button className="confirm-btn confirm-btn-no" onClick={handleCancelConfirm}>
              Cancel
            </button>
          </div>
        </div>
      );
    }

    switch (dynamic.status) {
      case 'not_uploaded':
        return (
          <div 
            className="status-box status-box-upload"
            onClick={handleClick}
          >
            <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="status-box-text">Click or drag to upload file</span>
          </div>
        );
      case 'revision_requested':
      case 'rejected':
        return (
          <div 
            className="status-box status-box-upload status-box-reupload"
            onClick={handleClick}
          >
            <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="status-box-text">Click or drag to re-upload file</span>
          </div>
        );
      case 'pending_review':
        return (
          <div className="status-box status-box-pending">
            <div className="status-box-main">
              <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="status-box-text">Uploaded — waiting for review</span>
            </div>
            <button className="status-box-replace" onClick={handleClick}>
              Upload a different file
            </button>
          </div>
        );
      case 'approved':
        return (
          <div className="status-box status-box-approved">
            <div className="status-box-main">
              <svg className="status-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span className="status-box-text">Approved</span>
            </div>
            <button className="status-box-replace" onClick={handleClick}>
              Upload a different file
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`dynamic-card ${needsAttention ? 'needs-attention' : ''} ${dynamic.status === 'approved' ? 'is-approved' : ''} ${isDragging ? 'is-dragging' : ''}`}
      onDragEnter={handleCardDragEnter}
      onDragOver={handleCardDragOver}
      onDragLeave={handleCardDragLeave}
      onDrop={handleCardDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/wav,audio/wave,.wav"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      <div className="dynamic-header">
        <h4 className="dynamic-name">{displayName}</h4>
      </div>

      <div className="dynamic-content">
        {needsAttention && (
          <ReviewNotes notes={dynamic.review_notes} status={dynamic.status} />
        )}

        {/* Simple View PDF Sheet Music button */}
        <a 
          href={scoreUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="score-button"
          onClick={(e) => e.stopPropagation()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          View PDF Sheet Music
        </a>

        {/* Simple instruction text */}
        <p className="dynamic-instructions">
          {dynamicDisplayName 
            ? `Play the score with articulation "${articulationName}" at dynamic "${dynamic.dynamic_level}".`
            : `Play the score with articulation "${articulationName}".`
          }
        </p>

        {/* Expected filename */}
        <p className="dynamic-filename">
          Save as: <code>{dynamic.expected_filename}</code>
        </p>

        {/* Unified status box */}
        {renderStatusBox()}
      </div>
    </div>
  );
};

export default DynamicCard;
